import axios, { AxiosResponse } from 'axios';
import { setLoginRedirectNotice } from '../authNotices';
import {
  LoginResponse,
  User,
  AnalysisResult,
  AnalysisCapabilities,
  MonitoringStatus,
  Event,
  DailyReport,
  DetectionFeedbackCreate,
  DetectionFeedbackReviewStatus,
  DetectionFeedbackRow,
  FusionIngestRow,
  ConsoleHealth,
  PilotReadiness,
} from '../types/api';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 요청 인터셉터 - 토큰 추가
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// 응답 인터셉터 - 에러 처리
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const reqPath = String(error.config?.url ?? '');
      if (reqPath.includes('auth/login')) {
        return Promise.reject(error);
      }
      if (typeof window !== 'undefined' && window.location.pathname.startsWith('/login')) {
        return Promise.reject(error);
      }
      localStorage.removeItem('token');
      setLoginRedirectNotice();
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  login: (username: string, password: string): Promise<AxiosResponse<LoginResponse>> =>
    api.post('/auth/login', { username, password }),
  logout: (): Promise<AxiosResponse<void>> => api.post('/auth/logout'),
  getCurrentUser: (): Promise<AxiosResponse<User>> => api.get('/auth/me'),
};

export const analysisAPI = {
  analyzeFile: (
    file: File,
    opts?: {
      signal?: AbortSignal;
      onUploadProgress?: (percent: number) => void;
    }
  ): Promise<AxiosResponse<{ id: string }>> => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/analysis/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 600_000,
      signal: opts?.signal,
      onUploadProgress: (event) => {
        if (!opts?.onUploadProgress || !event.total) return;
        opts.onUploadProgress(Math.min(100, Math.round((event.loaded * 100) / event.total)));
      },
    });
  },
  getAnalysisResult: (id: string): Promise<AxiosResponse<AnalysisResult>> =>
    api.get(`/analysis/result/${id}`),
  getAnalysisStatus: (id: string): Promise<AxiosResponse<{ status: string; error?: string; source?: string; file?: string }>> =>
    api.get(`/analysis/status/${id}`),
  getCapabilities: (): Promise<AxiosResponse<AnalysisCapabilities>> =>
    api.get('/analysis/capabilities', { timeout: 8000 }),
  /** 엣지·워커 → Console upsert (Bearer 필요). `/try?analysis_id=` 와 동일 id. */
  syncAnalysis: (body: {
    id: string;
    status: 'processing' | 'completed' | 'failed';
    result?: AnalysisResult;
    source?: string;
    file?: string;
    error?: string;
    media_kind?: 'image' | 'video';
  }): Promise<AxiosResponse<{ id: string; status: string; source?: string }>> =>
    api.post('/v1/analysis/sync', body),
};

export const monitoringAPI = {
  getStatus: (): Promise<AxiosResponse<MonitoringStatus>> => 
    api.get('/monitoring/status'),
  getEvents: (params: { page: number; limit: number }): Promise<AxiosResponse<{ events: Event[]; total: number }>> =>
    api.get('/monitoring/events', { params }),
  getParkingMap: (): Promise<AxiosResponse<MonitoringStatus['parking_map']>> => 
    api.get('/monitoring/parking-map'),
  getEventDetails: (eventId: string): Promise<AxiosResponse<Event>> =>
    api.get(`/monitoring/events/${eventId}`),
};

export const reportAPI = {
  getDailyReport: (date: string): Promise<AxiosResponse<DailyReport>> =>
    api.get('/reports/daily', { params: { date } }),
  getEventReport: (eventId: string): Promise<AxiosResponse<Event>> =>
    api.get(`/reports/events/${eventId}`),
  getReportRange: (startDate: string, endDate: string): Promise<AxiosResponse<DailyReport[]>> =>
    api.get('/reports/range', { params: { start_date: startDate, end_date: endDate } }),
};

export const feedbackAPI = {
  submitDetectionFeedback: (
    body: DetectionFeedbackCreate
  ): Promise<
    AxiosResponse<{ id: string; created_at: string; review_status?: string }>
  > => api.post('/v1/detections/feedback', body),
  listDetectionFeedback: (
    params?: { limit?: number; review_status?: string }
  ): Promise<AxiosResponse<{ items: DetectionFeedbackRow[]; total: number }>> =>
    api.get('/v1/detections/feedback', { params }),
  patchDetectionFeedbackReview: (
    feedbackId: string,
    body: { review_status: DetectionFeedbackReviewStatus },
  ): Promise<AxiosResponse<DetectionFeedbackRow>> =>
    api.patch(`/v1/detections/feedback/${feedbackId}`, body),
  bulkReviewDetectionFeedback: (body: {
    from_review_status: DetectionFeedbackReviewStatus;
    to_review_status: DetectionFeedbackReviewStatus;
    limit?: number;
  }): Promise<
    AxiosResponse<{
      updated: number;
      from_review_status: string;
      to_review_status: string;
      limit: number;
    }>
  > => api.post('/v1/detections/feedback/bulk-review', body),
  exportConfirmedFeedback: (body?: {
    limit?: number;
    held_out_event_ids?: string[];
    held_out_split?: string;
  }): Promise<
    AxiosResponse<{
      exported: Record<string, unknown>[];
      isolated: Record<string, unknown>[];
      total_confirmed_listed: number;
    }>
  > => api.post('/v1/detections/feedback/export', body ?? { limit: 200 }),
};

export const evaluationAPI = {
  evaluateZoneEvents: (
    body: Record<string, unknown>
  ): Promise<AxiosResponse<Record<string, unknown>>> =>
    api.post('/v1/evaluation/zone-events', body),
};

export const fusionAPI = {
  listIngest: (
    params?: { limit?: number },
  ): Promise<AxiosResponse<{ items: FusionIngestRow[]; total: number }>> =>
    api.get('/v1/fusion/ingest', { params }),
  submitIngest: (body: {
    source_system_id: string;
    observed_at?: string;
    trust_tier?: number;
    latency_class?: string;
    raw_ref?: string;
    payload?: Record<string, unknown>;
  }): Promise<
    AxiosResponse<{
      id: string;
      status: string;
      bus_topic: string;
      source_system_id: string;
    }>
  > => api.post('/v1/fusion/ingest', body),
};

export const edgeAPI = {
  pushEvent: (body: {
    device_id?: string;
    source_system_id?: string;
    site_id?: string;
    event_type?: string;
    camera_id?: string;
    track_id?: string;
    agent?: { summary?: string };
    agent_summary?: string;
  }): Promise<
    AxiosResponse<{
      id: string;
      status: string;
      ingest_path: string;
      source_system_id: string;
    }>
  > => api.post('/v1/edge/events', body),
  pushHeartbeat: (body: {
    device_id: string;
    stream_status?: string;
    camera_id?: string;
    source_kind?: string;
  }): Promise<AxiosResponse<{ status: string; device_id: string; stream_status: string }>> =>
    api.post('/v1/edge/heartbeat', body),
};

export const platformAPI = {
  getHealth: (): Promise<AxiosResponse<ConsoleHealth>> => api.get('/health', { timeout: 8000 }),
  getPilotReadiness: (): Promise<AxiosResponse<PilotReadiness>> =>
    api.get('/v1/pilot/readiness', { timeout: 12000 }),
  getAuditEvents: (limit = 50): Promise<AxiosResponse<{ enabled: boolean; items: unknown[] }>> =>
    api.get('/v1/audit/events', { params: { limit }, timeout: 8000 }),
};

export type { ConsoleHealth, PilotReadiness } from '../types/api';

export default api; 